/**
 * User management API schemas
 */

import { z } from 'zod';
import { Role, Status } from '@pillar-portal/database/dist/frontend';
import { PositiveIntSchema } from './common.schemas';
import {
    PermissionGrantSchema,
    RoleAssignmentDetailedSchema,
    RoleAssignmentPermissionsViewSchema,
} from '../permission.types';
import type {
    AssignRoleRequest,
    CreateOrganizationalUserCreatedUser,
    CreateOrganizationalUserRequest,
    CreateOrganizationalUserResponse,
    CreateInternalUserCreatedUser,
    CreateInternalUserResponse,
    RevokeRoleRequest,
    UserProfileDetailedPublicResponse,
    UserProfilePublicResponse,
    UpdateRoleAssignmentRequest,
    UpdateUserResponse,
    UpdateRoleAssignmentBodyRequest,
} from '../user.types';

/**
 * Create user request (unified for both IUser and OUser)
 */
export const CreateUserRequestSchema = z.object({
    primary_email: z.string().email(),
    emails: z.array(z.string().email()),
    role: z.enum(Role),
    organization_id: z.number().nullable(), // Required for organizational roles, null/undefined for internal users
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    skip_verification: z.boolean().optional(),
});

/**
 * Create user response
 */
export const CreateUserResponseSchema = z.object({
    user: z.lazy(() => UserProfileDetailedResponseSchema).nullable(),
    invitation_token: z.string().nullable(),
    invitation_expires_at: z.string().nullable(), // ISO date string
    primary_email: z.string().email(),
});

export const CreateInternalUserCreatedUserSchema: z.ZodType<CreateInternalUserCreatedUser> =
    z.object({
        user_uuid: z.string().uuid(),
    });

export const CreateInternalUserResponseSchema: z.ZodType<CreateInternalUserResponse> = z.object({
    user: CreateInternalUserCreatedUserSchema.nullable(),
    invitation_token: z.string().nullable(),
    invitation_expires_at: z.string().nullable(), // ISO date string
    primary_email: z.string().email(),
});

export const CreateOrganizationalUserRequestSchema: z.ZodType<CreateOrganizationalUserRequest> =
    z.object({
        primary_email: z.string().email(),
        emails: z.array(z.string().email()),
        role: z.enum(Role),
        organization_uuid: z.string().uuid(),
        first_name: z.string().nullable(),
        last_name: z.string().nullable(),
        skip_verification: z.boolean().optional(),
    });

export const CreateOrganizationalUserCreatedUserSchema: z.ZodType<CreateOrganizationalUserCreatedUser> =
    z.object({
        user_uuid: z.string().uuid(),
    });

export const CreateOrganizationalUserResponseSchema: z.ZodType<CreateOrganizationalUserResponse> =
    z.object({
        user: CreateOrganizationalUserCreatedUserSchema.nullable(),
        invitation_token: z.string().nullable(),
        invitation_expires_at: z.string().nullable(), // ISO date string
        primary_email: z.string().email(),
    });

/**
 * Update user request
 */
export const UpdateUserRequestSchema = z.object({
    primary_email: z.string().email().optional(),
    status: z.enum(Status).optional(),
    first_name: z.string().nullable().optional(),
    last_name: z.string().nullable().optional(),
});

export const UpdateUserResponseSchema: z.ZodType<UpdateUserResponse> = z.object({
    user_uuid: z.string().uuid(),
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    primary_email: z.string(),
    emails: z.array(z.string()),
    totp_enabled: z.boolean(),
    status: z.string(),
    invitation_token: z.string().optional(),
});

/**
 * User profile response (basic, without role assignments)
 */
export const UserProfileResponseSchema = z.object({
    user_id: z.number(),
    user_uuid: z.string().uuid().optional(),
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    primary_email: z.string(),
    emails: z.array(z.string()),
    totp_enabled: z.boolean(),
    status: z.string(), // Note: API returns string, not Status enum
    invitation_token: z.string().optional(),
});

/**
 * User profile response with full details (includes role assignments)
 */
export const UserProfileDetailedResponseSchema = UserProfileResponseSchema.extend({
    default_role_assignment: RoleAssignmentDetailedSchema.nullable(),
    other_role_assignments: z.array(RoleAssignmentDetailedSchema),
});

export const UserProfilePublicResponseSchema: z.ZodType<UserProfilePublicResponse> = z.object({
    user_uuid: z.string().uuid(),
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    primary_email: z.string(),
    emails: z.array(z.string()),
    totp_enabled: z.boolean(),
    status: z.string(),
    invitation_token: z.string().optional(),
});

export const UserProfileDetailedPublicResponseSchema: z.ZodType<UserProfileDetailedPublicResponse> =
    z.object({
        user_uuid: z.string().uuid(),
        first_name: z.string().nullable(),
        last_name: z.string().nullable(),
        primary_email: z.string(),
        emails: z.array(z.string()),
        totp_enabled: z.boolean(),
        status: z.string(),
        invitation_token: z.string().optional(),
        default_role_assignment: RoleAssignmentPermissionsViewSchema.nullable(),
        other_role_assignments: z.array(RoleAssignmentPermissionsViewSchema),
    });

/**
 * Assign role request
 */
export const AssignRoleRequestSchema: z.ZodType<AssignRoleRequest> = z.object({
    user_uuid: z.string().uuid(),
    role: z.enum(Role),
    organization_uuid: z.string().uuid().nullable(), // Required for organizational roles, null for global roles
    permissions: z.array(PermissionGrantSchema).optional(), // Optional permissions to grant immediately
});

/**
 * Revoke role request
 */
export const RevokeRoleRequestSchema: z.ZodType<RevokeRoleRequest> = z.object({
    role_assignment_uuid: z.string().uuid(),
    user_uuid: z.string().uuid(), // Cross-check for safety
});

/**
 * Update role assignment request
 */
export const UpdateRoleAssignmentBodyRequestSchema: z.ZodType<UpdateRoleAssignmentBodyRequest> =
    z.object({
        user_uuid: z.string().uuid(), // Cross-check for safety
        role: z.enum(Role).optional(),
        profile_name: z.string().optional(), // Deprecated - not supported in current schema
        user_config: z.record(z.string(), z.unknown()).optional(),
    });

export const UpdateRoleAssignmentRequestSchema: z.ZodType<UpdateRoleAssignmentRequest> = z.object({
    role_assignment_uuid: z.string().uuid(),
    user_uuid: z.string().uuid(), // Cross-check for safety
    role: z.enum(Role).optional(),
    profile_name: z.string().optional(), // Deprecated - not supported in current schema
    user_config: z.record(z.string(), z.unknown()).optional(),
});

/**
 * Delete my account request (self-delete)
 */
export const DeleteMyAccountRequestSchema = z.object({
    password: z.string().min(1),
});

/**
 * Delete my account response
 */
export const DeleteMyAccountResponseSchema = z.object({
    success: z.boolean(),
    message: z.string(),
});

/**
 * Get profile response (simplified user profile)
 */
export const GetProfileResponseSchema = z.object({
    user: z
        .object({
            user_uuid: z.string().uuid(),
            primary_email: z.string(),
            emails: z.array(z.string()),
            status: z.string(),
            totp_enabled: z.boolean(),
        })
        .nullable(),
});

/**
 * Update profile request
 */
export const UpdateProfileRequestSchema = z.object({
    primary_email: z.string().email(),
});

/**
 * Reset password admin request
 */
export const ResetPasswordAdminRequestSchema = z.object({
    new_password: z.string().min(1),
});

/**
 * Set user emails request
 */
export const SetUserEmailsRequestSchema = z
    .object({
        emails: z.array(z.string().email()).min(1),
        primary_email: z.string().email(),
    })
    .superRefine((value, context) => {
        const uniqueEmails = new Set<string>(value.emails);
        if (uniqueEmails.size !== value.emails.length) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'emails must be unique',
                path: ['emails'],
            });
        }
        if (!uniqueEmails.has(value.primary_email)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'primary_email must be included in emails',
                path: ['primary_email'],
            });
        }
    });

export const RequestAddEmailVerificationRequestSchema = z.object({
    email: z.string().email(),
    skip_verification: z.boolean().optional(),
});

export const RequestAddEmailVerificationResponseSchema = z.object({
    message: z.string(),
    verification_link: z.string().optional(),
});

/**
 * Get emails response
 */
export const GetEmailsResponseSchema = z.object({
    primary_email: z.string(),
    emails: z.array(
        z.object({
            email: z.string(),
            is_primary: z.boolean(),
            is_verified: z.boolean(),
        })
    ),
});

/**
 * Get my roles response
 */
export const GetMyRolesResponseSchema = z.object({
    roles: z.array(
        z.object({
            role: z.enum(Role),
            organization_uuid: z.string().uuid().nullable(),
            organization_name: z.string().optional(),
        })
    ),
});

/**
 * Common param schemas
 */
export const UserIdParamSchema: z.ZodType<number> = PositiveIntSchema;
export const UserUuidParamSchema: z.ZodType<string> = z.string().uuid();
export const RoleAssignmentIdParamSchema: z.ZodType<number> = PositiveIntSchema;
